import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Building2, ChevronRight, FileText } from "lucide-react";
import { useTranslation } from 'react-i18next';

// Location row component for reuse
function LocationRow({ label, data, onClick, testId, showRejected = false }) {
  const { t } = useTranslation();
  
  return (
    <div
      className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer transition-colors"
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-slate-500" />
        <span className="font-medium text-slate-900">{label}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-600">{data.total} {t('locationReports.applications')}</span>
        <div className="flex gap-2">
          <Badge className="bg-yellow-100 text-yellow-800 text-xs">{data.pending} {t('locationReports.pendingShort')}</Badge>
          <Badge className="bg-emerald-100 text-emerald-800 text-xs">{data.approved} {t('locationReports.approvedShort')}</Badge>
          {showRejected && (
            <Badge className="bg-red-100 text-red-800 text-xs">{data.rejected} {t('locationReports.rejectedShort')}</Badge>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </div>
    </div>
  );
}

export default function LocationSummaryView({
  locationStats,
  currentLevel,
  selectedDistrict,
  selectedTaluk,
  selectedHobli,
  selectedCircle,
  setSelectedDistrict,
  setSelectedTaluk,
  setSelectedHobli,
  setSelectedCircle,
  setSelectedVillage,
  setViewMode,
  filteredApplications,
  hoblis = [],  // Add hoblis list for key lookup
  circles = [], // Add circles list
  villages = [], // Add villages list for key lookup
  matchesHobli, // Add matchesHobli function for hobli comparison
  user,  // User object to check department
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'kn' ? 'kn' : 'en';
  
  // Check if user is from Survey department (no Circle concept for Survey)
  const isSurveyUser = user?.department === 'survey';

  // All Madabal/Madabalu variants for matching
  const MADABAL_VARIANTS = ['ಮಾಡಬಾಳು', 'ಮಾಡಬಾಳ್', 'ಮಾದಬಾಳು', 'Madabalu', 'Madabal', 'MADABALU', 'MADABAL'];
  
  // Helper to check if a name is a Madabal variant
  const isMadabalVariant = (name) => {
    if (!name) return false;
    return MADABAL_VARIANTS.includes(name) || 
           name.toUpperCase() === 'MADABALU' || 
           name.toUpperCase() === 'MADABAL';
  };

  // Helper to find the correct key from locations list (case-insensitive match)
  const findLocationKey = (list, name) => {
    if (!name || !list || list.length === 0) return name;
    
    // Special case: if name is a Madabal variant, find the Madabalu entry
    if (isMadabalVariant(name)) {
      const madabalMatch = list.find(loc => {
        if (typeof loc === 'string') return isMadabalVariant(loc);
        return isMadabalVariant(loc.key) || isMadabalVariant(loc.name_en) || isMadabalVariant(loc.name_kn);
      });
      if (madabalMatch) {
        return typeof madabalMatch === 'string' ? madabalMatch : madabalMatch.key;
      }
    }
    
    // Try to find matching location by name (case-insensitive)
    const match = list.find(loc => {
      if (typeof loc === 'string') return loc.toUpperCase() === name.toUpperCase();
      const key = loc.key || '';
      const nameEn = loc.name_en || '';
      const nameKn = loc.name_kn || '';
      return key.toUpperCase() === name.toUpperCase() || 
             nameEn.toUpperCase() === name.toUpperCase() ||
             nameKn === name;
    });
    if (match) {
      return typeof match === 'string' ? match : match.key;
    }
    // If no match found, return original name as-is (don't uppercase for Kannada text)
    return name;
  };

  // No data message
  if (filteredApplications.length === 0) {
    return (
      <Card className="p-8 bg-white border border-stone-200 text-center">
        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500">{t('locationReports.noApplications')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* District Level */}
      {currentLevel === "all" && Object.keys(locationStats.byDistrict).length > 0 && (
        <Card className="p-4 bg-white border border-stone-200">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-700" />
            {t('locationReports.districtWise')}
          </h3>
          <div className="space-y-2">
            {Object.entries(locationStats.byDistrict).map(([district, data]) => (
              <LocationRow
                key={district}
                label={district}
                data={data}
                onClick={() => setSelectedDistrict(district)}
                testId={`district-row-${district}`}
                showRejected
              />
            ))}
          </div>
        </Card>
      )}

      {/* Taluk Level */}
      {currentLevel === "district" && Object.keys(locationStats.byTaluk).length > 0 && (
        <Card className="p-4 bg-white border border-stone-200">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-700" />
            {t('locationReports.talukWise')} - {selectedDistrict}
          </h3>
          <div className="space-y-2">
            {Object.values(locationStats.byTaluk)
              .filter(data => data.district?.toUpperCase() === selectedDistrict?.toUpperCase())
              .map((data) => (
                <LocationRow
                  key={data.taluk}
                  label={`${data.taluk} ${t('newApplication.talukSuffix')}`}
                  data={data}
                  onClick={() => setSelectedTaluk(data.taluk)}
                  testId={`taluk-row-${data.taluk}`}
                />
              ))}
          </div>
        </Card>
      )}

      {/* Hobli Level */}
      {currentLevel === "taluk" && Object.keys(locationStats.byHobli).length > 0 && (
        <Card className="p-4 bg-white border border-stone-200">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-purple-700" />
            {t('locationReports.hobliWise')} - {selectedTaluk} {t('newApplication.talukSuffix')}
          </h3>
          <div className="space-y-2">
            {Object.values(locationStats.byHobli)
              .filter(data => data.district?.toUpperCase() === selectedDistrict?.toUpperCase() && data.taluk?.toUpperCase() === selectedTaluk?.toUpperCase())
              .map((data) => (
                <LocationRow
                  key={data.hobli}
                  label={`${data.hobli} ${t('newApplication.hobliSuffix')}`}
                  data={data}
                  onClick={() => {
                    // Use findLocationKey to get the correct key format for the filter
                    const hobliKey = findLocationKey(hoblis, data.hobli);
                    setSelectedHobli(hobliKey);
                    // For Survey users: Stay in Summary View to show Village-wise
                    // For Revenue users: Switch to Detailed View
                    if (!isSurveyUser) {
                      setViewMode("detailed");
                    }
                    // Survey users stay in Summary View - will show Village-wise next
                  }}
                  testId={`hobli-row-${data.hobli}`}
                />
              ))}
          </div>
        </Card>
      )}

      {/* Circle Level - Only for Revenue users, not for Survey */}
      {!isSurveyUser && currentLevel === "hobli" && Object.keys(locationStats.byCircle).length > 0 && (
        <Card className="p-4 bg-white border border-stone-200">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-teal-700" />
            {t('locationReports.circleWise') || 'ವೃತ್ತವಾರು ಅರ್ಜಿಗಳು'} - {selectedHobli} {t('newApplication.hobliSuffix')}
          </h3>
          <div className="space-y-2">
            {Object.values(locationStats.byCircle)
              .filter(data => {
                const districtMatch = data.district?.toUpperCase() === selectedDistrict?.toUpperCase();
                const talukMatch = data.taluk?.toUpperCase() === selectedTaluk?.toUpperCase();
                // Use matchesHobli if provided, otherwise fallback to simple comparison
                const hobliMatch = matchesHobli ? matchesHobli(data.hobli, selectedHobli) : data.hobli?.toUpperCase() === selectedHobli?.toUpperCase();
                return districtMatch && talukMatch && hobliMatch;
              })
              .map((data) => (
                <LocationRow
                  key={data.circle}
                  label={data.circle}
                  data={data}
                  onClick={() => {
                    setSelectedCircle(data.circle);
                  }}
                  testId={`circle-row-${data.circle}`}
                />
              ))}
          </div>
        </Card>
      )}

      {/* Village Level - For Revenue: shown after Circle selection; For Survey: shown after Hobli selection */}
      {((isSurveyUser && currentLevel === "hobli") || (!isSurveyUser && currentLevel === "circle")) && Object.keys(locationStats.byVillage).length > 0 && (
        <Card className="p-4 bg-white border border-stone-200">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-orange-700" />
            {t('locationReports.villageWise')} - {isSurveyUser ? selectedHobli : selectedCircle} {isSurveyUser ? t('newApplication.hobliSuffix') : (t('locationReports.circleSuffix') || 'ವೃತ್ತ')}
          </h3>
          <div className="space-y-2">
            {Object.values(locationStats.byVillage)
              .filter(data => {
                const districtMatch = data.district?.toUpperCase() === selectedDistrict?.toUpperCase();
                const talukMatch = data.taluk?.toUpperCase() === selectedTaluk?.toUpperCase();
                const hobliMatch = matchesHobli ? matchesHobli(data.hobli, selectedHobli) : data.hobli?.toUpperCase() === selectedHobli?.toUpperCase();
                // For Survey users, skip circle matching
                if (isSurveyUser) {
                  return districtMatch && talukMatch && hobliMatch;
                }
                const circleMatch = data.circle === selectedCircle;
                return districtMatch && talukMatch && hobliMatch && circleMatch;
              })
              .map((data) => (
                <LocationRow
                  key={data.village}
                  label={data.village}
                  data={data}
                  onClick={() => {
                    // Use the village name directly from applications data for accurate filtering
                    // Don't transform it - use exact match to filter applications
                    setSelectedVillage(data.village);
                    setViewMode("detailed"); // Auto-switch to detailed view
                  }}
                  testId={`village-row-${data.village}`}
                />
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}
